/**
 * features/suppliers/hooks.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupplierInput } from '@ft/core';
import { api, queryString } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { Paginated, Party } from '~/lib/types';

export interface SupplierListParams {
  page: number;
  pageSize: number;
  search: string;
}

export function useSuppliers(params: SupplierListParams) {
  return useQuery({
    queryKey: queryKeys.suppliers(params),
    queryFn: () =>
      api.get<Paginated<Party>>(
        `/api/suppliers${queryString({ page: params.page, pageSize: params.pageSize, search: params.search })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierInput) => api.post<Party>('/api/suppliers', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SupplierInput }) =>
      api.put<Party>(`/api/suppliers/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/suppliers/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}
